import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { Button, HStack, Text, VStack } from '@squire/ui';

import { ReviewStarsBubbleIcon } from '@/components/_icons/ReviewStarsBubbleIcon';
import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { CloseButton } from '@/components/_shared/CloseButton';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { Tracker } from '@/utils/tracking';

import { styles } from './styles';
import type { AppStoreReviewPromptProps } from './types';

/**
 * Custom "do you like the app?" interstitial shown before the OS review prompt,
 * mirroring native iOS `ReviewPromptView`. Positive intent routes to the native
 * review prompt; negative intent routes to a feedback email.
 */
export const AppStoreReviewPrompt = forwardRef<
  BottomSheetRef,
  AppStoreReviewPromptProps
>(({ onLoveIt, onNeedsWork, onDismiss }, ref) => {
  const { t } = useScreenTranslations('AppStoreReviewPrompt');
  const internalRef = useRef<BottomSheetRef>(null);
  // Distinguishes an explicit button action from a plain close/swipe dismiss.
  const actionTakenRef = useRef(false);

  useImperativeHandle(ref, () => internalRef.current as BottomSheetRef);

  // Fires after the BottomSheet close animation completes (swipe or programmatic).
  const handleSheetDismissed = () => {
    if (!actionTakenRef.current) {
      Tracker.track('in_app_review.close');
    }
    actionTakenRef.current = false;
    onDismiss?.();
  };

  const handleLoveItPress = () => {
    actionTakenRef.current = true;
    Tracker.track('in_app_review.love_it');
    onLoveIt();
    internalRef.current?.close();
  };

  const handleNeedsWorkPress = () => {
    actionTakenRef.current = true;
    Tracker.track('in_app_review.needs_work');
    onNeedsWork();
    internalRef.current?.close();
  };

  const handleClosePress = () => {
    internalRef.current?.close();
  };

  return (
    <BottomSheet ref={internalRef} onDismiss={handleSheetDismissed} dismissible>
      <VStack style={styles.content}>
        <HStack style={styles.closeRow}>
          <CloseButton
            testID="app-store-review-close"
            onPress={handleClosePress}
            accessibilityLabel={t('accessibility.closeButton')}
            accessibilityHint={t('accessibility.closeButtonHint')}
          />
        </HStack>

        <VStack style={styles.header}>
          <Text variant="3xlStrong" color="textPrimary" style={styles.title}>
            {t('title')}
          </Text>
          <ReviewStarsBubbleIcon />
          <Text variant="m" color="textSecondary" style={styles.description}>
            {t('description')}
          </Text>
        </VStack>

        <VStack style={styles.actions}>
          <Button
            testID="app-store-review-love-it"
            variant="primary"
            onPress={handleLoveItPress}
          >
            {t('primaryButton')}
          </Button>
          <Button
            testID="app-store-review-needs-work"
            variant="secondary"
            onPress={handleNeedsWorkPress}
          >
            {t('secondaryButton')}
          </Button>
        </VStack>
      </VStack>
    </BottomSheet>
  );
});

AppStoreReviewPrompt.displayName = 'AppStoreReviewPrompt';
